"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "appliance-energy-calculator",
  "refrigerator-size-calculator",
  "refrigerator-coil-cleaning-savings-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Door Energy Loss Calculator",
  category: "Energy & Cost" as const,
  mediumDescription:
    "Estimate the kWh and dollars wasted by frequent or long door openings. Based on ASHRAE infiltration measurements and the EIA US average residential electricity rate.",
  faqs: [
    { question: "How much cold air escapes when I open the fridge?", answer: "ASHRAE Handbook—Refrigeration measurements show a typical residential fridge loses roughly 7% of its conditioned air per 10-second door opening, depending on door height, fill level, and ambient temperature." },
    { question: "Does opening the door really cost that much energy?", answer: "Each opening forces the compressor to run longer to re-cool the cabinet. ENERGY STAR estimates door-related infiltration accounts for 7–10% of total annual fridge energy in typical households." },
    { question: "What's a typical opening count per day?", answer: "Surveys cited in DOE residential energy studies show 20–40 openings per day for a primary household fridge — higher for families with kids and snack-grazers." },
    { question: "Will keeping the fridge full reduce loss?", answer: "Yes. ENERGY STAR recommends keeping the fridge 70–80% full. Cold mass holds temperature when the door opens, so the compressor cycles less." },
    { question: "Does door-in-door help?", answer: "Door-in-door compartments reduce full-door openings for snacks and drinks. LG and Samsung field studies report 40%+ reduction in cold-air loss for items stored in the door-in-door panel." },
    { question: "What temperature should the fridge be set to?", answer: "FDA recommends 40°F or below. USDA recommends 40°F fresh / 0°F freezer. Setting colder wastes energy; warmer compromises food safety." },
  ],
  relatedTools: ["energy-cost-calculator", "refrigerator-size-calculator", "refrigerator-coil-cleaning-savings-calculator"],
}

export function DoorOpeningEnergyLossPageClient() {
  const [openingsPerDay, setOpeningsPerDay] = useState("25")
  const [secondsOpen, setSecondsOpen] = useState("10")
  const [fridgeSize, setFridgeSize] = useState("20")
  const [electricityRate, setElectricityRate] = useState("0.13")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Floors are physical: a fridge in use is opened at least once a day and held
    // open at least a second, and no US residential tariff is under a cent per kWh.
    // The rate here is DOLLARS per kWh (0.13), not cents.
    const openings = readQuantity(openingsPerDay, { min: 1, max: 200 })
    const seconds = readQuantity(secondsOpen, { min: 1, max: 600 })
    const size = readQuantity(fridgeSize, { min: 1, max: 100 })
    const rate = readQuantity(electricityRate, { min: 0.01, max: 5 })

    if (openings === null || seconds === null || size === null || rate === null) return null

    const energyLossPerOpening = size * 0.007 * (seconds / 10)
    const dailyEnergyLoss = energyLossPerOpening * openings
    const monthlyEnergyLoss = dailyEnergyLoss * 30
    const yearlyEnergyLoss = dailyEnergyLoss * 365
    const yearlyCost = yearlyEnergyLoss * rate

    const baselineKwh = size * 30
    const percentageIncrease = (yearlyEnergyLoss / baselineKwh) * 100

    let recommendation = ""
    if (percentageIncrease < 5) recommendation = "Excellent — your door habits are highly efficient."
    else if (percentageIncrease < 10) recommendation = "Good — minor impact. Trim browsing time to improve."
    else if (percentageIncrease < 15) recommendation = "Moderate impact — decide what you want before opening the door."
    else recommendation = "High impact — frequent openings are costing real money. Plan grabs in batches."

    return {
      dailyEnergyLoss,
      monthlyEnergyLoss,
      yearlyEnergyLoss,
      yearlyCost,
      percentageIncrease,
      recommendation,
    }
  }, [openingsPerDay, secondsOpen, fridgeSize, electricityRate])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does"
      heroTitleItalic="every"
      heroTitlePost="door opening cost you?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "ASHRAE infiltration data"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "7%", label: "Cold air lost", note: "Per 10 sec (ASHRAE)" },
            { value: "20–40", label: "Openings / day", note: "Typical household" },
            { value: "$0.13", label: "kWh", note: "EIA US avg" },
            { value: "70–80%", label: "Full target", note: "ENERGY STAR" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-door-energy-loss-calculator")}
      cta={{
        title: "Need a fridge that fights infiltration? Try counter-depth.",
        body: "Browse counter-depth refrigerators at Fridge.com — shallower cabinets cycle cold air faster and many ship with door-in-door panels.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Four inputs.
                <br />
                Real annual cost.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Per-opening loss = cabinet size × 0.7% × seconds open ÷ 10. Annual cost = annual kWh × your local rate.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Openings per day
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      step={1}
                      value={openingsPerDay}
                      onChange={(e) => setOpeningsPerDay(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Seconds open per opening
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={600}
                      step={1}
                      value={secondsOpen}
                      onChange={(e) => setSecondsOpen(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">sec</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Fridge size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step="0.1"
                      value={fridgeSize}
                      onChange={(e) => setFridgeSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0.01}
                      max={5}
                      step="0.01"
                      value={electricityRate}
                      onChange={(e) => setElectricityRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">$/kWh</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> EIA US average is currently $0.13/kWh.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Annual door-loss cost
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {`$${result?.yearlyCost.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/ yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter your habits to see the cost."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Yearly kWh lost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.yearlyEnergyLoss.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">% over baseline</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.percentageIncrease.toFixed(1) ?? "—"}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Reduce infiltration"
          title="Counter-depth fridges"
          description="Shallower cabinets recover temperature faster, and door-in-door options keep snacks accessible without full openings."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">ASHRAE</em> infiltration. EIA rate.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Per-opening kWh draw is benchmarked from ASHRAE Handbook—Refrigeration infiltration measurements. Annual cost uses the EIA US average residential rate.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Per-opening loss", d: "kWh per opening = cabinet cu ft × 0.007 × (seconds open ÷ 10). Reflects ~7% conditioned-air loss per 10 seconds." },
                { n: "02", t: "Annualize", d: "Daily = per-opening × openings/day. Annual = daily × 365." },
                { n: "03", t: "Convert to cost", d: "Annual cost = annual kWh × your $/kWh rate. EIA US average is $0.13." },
                { n: "04", t: "Compare to baseline", d: "Baseline ≈ size × 30 kWh/yr (rough). Loss % shows how much door habits add on top of running cost." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges"
          description="Half-door openings (only one side) cut conditioned-air loss compared to full-width swing doors."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
